/**
 * Qualification Card
 * Shows how referred friends can qualify
 */

import { Scan, Trophy, Star, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { QUALIFICATION } from '../utils/constants';
import type { QualificationRequirements } from '../types';

interface QualificationCardProps {
  requirements?: QualificationRequirements;
}

interface MethodCardProps {
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  progress?: {
    current: number;
    required: number;
    isComplete: boolean;
  };
  showOr?: boolean;
}

function MethodCard({
  icon: Icon,
  iconBg,
  iconColor,
  title,
  description,
  progress,
  showOr,
}: MethodCardProps) {
  return (
    <div className="relative flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
      {/* OR divider (hidden on mobile, visible between cards on desktop) */}
      {showOr && (
        <div className="hidden md:block absolute top-1/2 -left-6 -translate-y-1/2 bg-white px-2 text-sm font-medium text-gray-400 z-10">
          OR
        </div>
      )}

      <div className={`p-2 ${iconBg} rounded-lg shrink-0`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-medium text-gray-900">{title}</div>
          {progress?.isComplete && (
            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
          )}
        </div>
        <div className="text-sm text-gray-500">{description}</div>

        {/* Progress bar */}
        {progress && !progress.isComplete && (
          <div className="mt-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>{progress.current} / {progress.required}</span>
              <span>{Math.round((progress.current / progress.required) * 100)}%</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((progress.current / progress.required) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function QualificationCard({ requirements }: QualificationCardProps) {
  const { t } = useLanguage();

  const methods = [
    {
      icon: Scan,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      title: t('referrals.scanAsIsItems', `Scan ${QUALIFICATION.AS_IS_SCANS_REQUIRED} As-Is Items`),
      description: t(
        'referrals.scanAsIsItemsDescription',
        'Visit an IKEA store and scan items in the As-Is section'
      ),
      progress: requirements?.asIsScans
        ? {
            current: requirements.asIsScans.current,
            required: requirements.asIsScans.required,
            isComplete: requirements.asIsScans.isComplete,
          }
        : undefined,
      showOr: false,
    },
    {
      icon: Trophy,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      title: t('referrals.reachLevel2', `Reach Level ${QUALIFICATION.LEVEL_REQUIRED}`),
      description: t(
        'referrals.reachLevel2Description',
        `Earn ${QUALIFICATION.XP_REQUIRED.toLocaleString()} XP through shares, scans, or other activities`
      ),
      progress: requirements?.levelBased
        ? {
            current: requirements.levelBased.currentXp,
            required: requirements.levelBased.requiredXp,
            isComplete: requirements.levelBased.isComplete,
          }
        : undefined,
      showOr: true,
    },
    {
      icon: Star,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      title: t('referrals.upgradeToPro', 'Upgrade to Pro'),
      description: t(
        'referrals.upgradeToProDescription',
        'Subscribe to Pro or Pro+ for instant qualification'
      ),
      showOr: true,
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('referrals.howReferralsQualify', 'How Friends Qualify')}
        </h2>
        <p className="text-sm text-gray-500">
          {t('referrals.friendsMustComplete', 'Your friends must complete one of these')}
        </p>
      </div>

      <div className="p-6">
        {/* Mobile: stacked with "OR" text between */}
        <div className="md:hidden space-y-4">
          {methods.map((method, index) => (
            <div key={index}>
              {index > 0 && (
                <div className="text-center text-sm font-medium text-gray-400 py-2">OR</div>
              )}
              <MethodCard {...method} showOr={false} />
            </div>
          ))}
        </div>

        {/* Desktop: 3-column grid with "OR" between */}
        <div className="hidden md:grid md:grid-cols-3 gap-4">
          {methods.map((method, index) => (
            <MethodCard key={index} {...method} />
          ))}
        </div>

        {/* Note */}
        <div className="mt-4 text-center text-sm text-primary-600 font-medium">
          {t('referrals.onlyOneRequired', 'Only ONE method required!')}
        </div>
      </div>
    </div>
  );
}
